/**
 * SOLID Review
 * S — Strong. One concern: buying match credits — the checkout asked for, the player sent to it, or why not.
 * O — Good. Another pack is the payment side's.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The button's state and its one action.
 * D — Strong. One use case, the client view, the request contexts, the page's address.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { IServicePageAddress } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const START_CHECKOUT: TClientUseCaseInput<'startCheckout'> = {};
const CHECKOUT_FAILED = 'Failed to start checkout. Please try again.';

interface IStateBuyCreditsOwn {
  readonly isLoading: boolean;
  readonly error: string | null;
}

export interface IStateBuyCredits extends IStateBuyCreditsOwn {
  /** Signed in: only a registered player can buy. */
  readonly registered: boolean;
  readonly matchCredits: number;
}

const READY: IStateBuyCreditsOwn = { isLoading: false, error: null };
const ASKING: Partial<IStateBuyCreditsOwn> = { error: null, isLoading: true };

/**
 * THE CREDITS BUTTON (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/monetisation/BuyCreditsButton.tsx`'s state and
 * handler: the checkout is the payment side's use case (the token read inside Infrastructure, strict-ports FE4c); its answer sends the
 * player to the checkout's page; a refusal reads in the server's words, anything else as the generic line.
 */
export class BuyCreditsViewModel implements IServiceViewModel<IStateBuyCredits> {
  private readonly state = new ObservableState<IStateBuyCreditsOwn>(READY);
  private shown: { readonly own: IStateBuyCreditsOwn; readonly registered: boolean; readonly matchCredits: number; readonly button: IStateBuyCredits } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'startCheckout'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
    private readonly address: Pick<IServicePageAddress, 'assign'>,
  ) {}

  readonly buy = async (): Promise<void> => {
    /* c8 ignore next -- the button is disabled={!registered}; unreachable when signed out */
    if (!this.getSnapshot().registered) return;
    this.state.update(ASKING);
    const answer = await this.useCases.startCheckout(this.requests.context(), START_CHECKOUT);
    if (answer.success) {
      this.address.assign(answer.value.checkoutUrl);
      return;
    }
    const failed: Partial<IStateBuyCreditsOwn> = { error: messageFor(answer.error, CHECKOUT_FAILED), isLoading: false };
    this.state.update(failed);
  };

  getSnapshot(): IStateBuyCredits {
    const own = this.state.get();
    const { account } = this.view.getSnapshot().session;
    const registered = account.kind === 'registered';
    const { matchCredits } = account;
    if (this.shown !== null && this.shown.own === own && this.shown.registered === registered && this.shown.matchCredits === matchCredits) {
      return this.shown.button;
    }
    const button: IStateBuyCredits = { ...own, registered, matchCredits };
    this.shown = { own, registered, matchCredits, button };
    return button;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {}
}
